import "./settingsModal.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Button,
  Form,
  Modal,
  InputGroup
} from "react-bootstrap";
import { useHistory } from "react-router-dom";
import { generateRoutePath } from "utils/generateRoutePath";
import { request } from "hooks/useOkoApi/useOkoApi";
import { logError, LogCategory } from "utils/sentryLogger";
import Select, { components } from "react-select";
import Clear from "../../../../assets/Clear.svg";
import { colors, constants, DOMAIN_REGEX, FORBIDDEN_EMAIL_DOMAIN_CHARS } from "utils";
import OKOCoreLogo from "../../../../assets/OKO_Core_Logotype2.svg";
import { signOut } from "firebase/auth";
import { auth } from "hooks/useFirebase";

export const SettingsModal = ({ isOpen, isOnboarding, setIsOpen }) => {
  const mountedRef = useRef(true);
  const [error, setError] = useState();
  const [schoolNotSetupError, setSchoolNotSetupError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [multipleSchools, setMultipleSchools] = useState(null);
  const [selectedSchoolId, setSelectedSchoolId] = useState(null);
  // Existing-educator multi-school switcher (separate from signup-time
  // `multipleSchools` which is domain-matched schools during signup).
  const [availableSchools, setAvailableSchools] = useState([]);
  const [activeSchoolId, setActiveSchoolId] = useState(null);
  const [isSwitchingSchool, setIsSwitchingSchool] = useState(false);
  const history = useHistory();
  const [formData, setFormData] = useState({
    school_name: "",
    grade_levels: [],
    participant_email_domain_filter: "",
  });
  const alertStyle = {
    padding: "8px",
  }

  const getEducatorAccountData = async () => {
    setIsLoading(true);
    try {
      const educatorData = await request("account/educator", {
        method: "GET",
      });

      if (educatorData.schools) {
        if (mountedRef.current) {
          setMultipleSchools(educatorData.schools);
          const emailDomain = auth.currentUser?.email?.split('@')[1] || "";
          setFormData((prev) => ({
            ...prev,
            participant_email_domain_filter: emailDomain,
            grade_levels: ["4"],
          }));
          setDataLoaded(true);
          setIsLoading(false);
        }
      } else {
        if (mountedRef.current) {
          setFormData(educatorData.config);
          setAvailableSchools(educatorData.available_schools || []);
          setActiveSchoolId(educatorData.active_school?.id || null);
        }
        setIsLoading(false);
        setDataLoaded(true);
      }
    } catch (e) {
      logError("Failed to get educator account data", e, {
        category: LogCategory.ERROR,
        endpoint: "account/educator",
        method: "GET",
        userId: auth.currentUser?.uid,
      });
      setIsLoading(false);
      const errorMessage = e?.detail || e?.message || "";
      if (errorMessage.includes("school hasn't been set up")) {
        signOut(auth);
        setSchoolNotSetupError(true);
      } else {
        setError(errorMessage || "An unknown error occurred.");
      }
      setDataLoaded(true);
    }
  };

  useEffect(() => {
    getEducatorAccountData();
    return () => {
      mountedRef.current = false;
    }
  }, []);

  const handleChange = (e) => {
    let { name, value } = e.target;
    if (name === "participant_email_domain_filter") {
      value = value.replace(FORBIDDEN_EMAIL_DOMAIN_CHARS, "");
      // Allow only one "."
      const dotCount = (value.match(/\./g) || []).length;
      if (dotCount > 1) {
        value = value.slice(0, value.lastIndexOf("."));
      }
    }
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleSelectGradeChange = (e) => {
    const value = e.map((i) => i.value);
    setFormData((prevState) => ({
      ...prevState,
      ["grade_levels"]: value,
    }));
  };

  const handleSchoolSelect = (selectedOption) => {
    if (selectedOption) {
      setSelectedSchoolId(selectedOption.value);
      setFormData((prevState) => ({
        ...prevState,
        school_name: selectedOption.label,
      }));
    }
  };

  const handleActiveSchoolChange = async (selectedOption) => {
    if (!selectedOption || selectedOption.value === activeSchoolId) return;
    setIsSwitchingSchool(true);
    try {
      const updated = await request("account/educator/active_school", {
        method: "PUT",
        payload: { school_id: selectedOption.value },
      });
      setActiveSchoolId(updated.active_school?.id || null);
      // Reload page so every hook (classrooms, sessions, etc.) refetches
      // under the new active-school scope.
      window.location.reload();
    } catch (e) {
      logError("Failed to switch active school", e, {
        category: LogCategory.ERROR,
        endpoint: "account/educator/active_school",
        method: "PUT",
        userId: auth.currentUser?.uid,
        schoolId: selectedOption.value,
      });
      setError({ active_school: e?.detail || "Failed to switch school" });
      setIsSwitchingSchool(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (multipleSchools && !selectedSchoolId) {
      setError({ school: "Please select your school" });
      return;
    }
    if (!DOMAIN_REGEX.test(formData.participant_email_domain_filter)) {
      setError({participant_email_domain_filter: "Enter a valid domain (e.g. school.org, classlink.k12.nj.us). " +
          "Each label can contain letters, digits, and internal hyphens; the final TLD must be alpha-only and at least 2 letters."})
      return;
    }
    setIsLoading(true);
    try {
      const payload = {
        config: formData,
      };
      if (selectedSchoolId) {
        payload.school_id = selectedSchoolId;
      }
      await request("account/educator", {
        payload,
      }).then(() => {
        setIsLoading(false);
        setIsOpen(false);
        if (isOnboarding) {
          history.push(generateRoutePath(constants.EDUCATOR_ROUTE_NAME));
        }
      });
    } catch (e) {
      logError("Failed to save educator account data", e, {
        category: LogCategory.ERROR,
        endpoint: "account/educator",
        method: "POST",
        userId: auth.currentUser?.uid,
        selectedSchoolId,
      });
      console.log(e);
      if (Array.isArray(e.detail)) {
        const errors = {};
        e.detail.forEach((error) => {
          errors[error.loc.pop()] = error.msg;
        });
        setError(errors);
      } else {
        setError({ general: e.detail || e.message || "An error occurred" });
      }
      setIsLoading(false);
    }
  };

  const getGradeOptions = () => {
    return (
      [
        { label: "Kindergarten", value: "K" },
        { label: "1st Grade", value: "1" },
        { label: "2nd Grade", value: "2" },
        { label: "3rd Grade", value: "3" },
        { label: "4th Grade", value: "4" },
        { label: "5th Grade", value: "5" },
        { label: "6th Grade", value: "6" },
        { label: "7th Grade", value: "7" },
        { label: "8th Grade", value: "8" },
        { label: "High School", value: "HS" },
      ]
    );
  };

  const selectStyles = {
    control: (styles) => ({
      ...styles,
      backgroundColor: "white",
      borderColor: "#ABB1BA" }),
    option: (styles, { isFocused, isSelected }) => {
      return {
        ...styles,
        backgroundColor: isFocused ? colors.gray100 : undefined,
        color: "#1D1F23",
        ':active': {
          ...styles[':active'],
          backgroundColor: isSelected ? 'black' : 'white',
        },
      };
    },
    multiValue: (styles) => {
      return {
        ...styles,
        backgroundColor: colors.nightlyWoods500,
        borderRadius: "8px",
        fontSize: "16px",
        fontWeight: 600,
        lineHeight: "20px",
        padding: "8px 16px",
      };
    },
    multiValueLabel: (styles) => ({
      ...styles,
      color: colors.gray100,
      padding: `0 !important`,
    }),
    multiValueRemove: (styles) => ({
      ...styles,
      paddingRight: `0 !important`,
      ':hover': {
        opacity: 0.9,
      },
    }),
  };

  const MultiValueRemove = (props) => {
    return (
      <components.MultiValueRemove {...props}>
        <img src={Clear} alt="Remove" />
      </components.MultiValueRemove>
    );
  };

  const saveButton = (width) =>
    <Button style={{
      ...width,
      backgroundColor: colors.nightlyWoods500,
      border: "none",
      padding: "12px 24px" }}
            type="submit">
      Save
    </Button>;

  const handleClose = () => {
    // The if condition prevents clicking outside or using ESC button for closing the Modal
    // even when the closeButton is hidden
    if (!isOnboarding) setIsOpen(false);
  };

  const buttons = () =>
    <div style={{ display: "flex", gap: "24px", justifyContent: "space-between" }}>
      <Button onClick={handleClose} style={{
        backgroundColor: colors.neutral300,
        border: "none",
        padding: "12px 24px",
        width: "100%" }} >
        Cancel
      </Button>
      <>{saveButton({ width: "100%"})}</>
    </div>;

  if (!dataLoaded) {
    return (
      <Modal className="settings-modal" show={isOpen} onHide={() => {}}>
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", padding: "64px" }}>
          <FontAwesomeIcon icon={faSpinner} spin size="5x" />
        </div>
      </Modal>
    );
  }

  if (schoolNotSetupError) {
    return (
      <Modal className="settings-modal" show={isOpen} onHide={() => {}}>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "24px", padding: "32px" }}>
          <img src={OKOCoreLogo} alt="OKO" style={{ width: "196px" }} />
          <p style={{ fontSize: "24px", fontWeight: 700, textAlign: "center" }}>
            Your school hasn't been set up yet
          </p>
          <p style={{ fontSize: "16px", textAlign: "center", color: colors.neutral400 }}>
            Please contact your school administrator to get started.
          </p>
          <Button
            href="/educator/login"
            style={{
              backgroundColor: colors.nightlyWoods500,
              border: "none",
              padding: "12px 24px"
            }}
          >
            Back to Login
          </Button>
        </div>
      </Modal>
    );
  }

  const schoolOptions = multipleSchools?.map((school) => ({
    value: school.id,
    label: school.name,
  })) || [];

  return (
    <Modal className="settings-modal" show={isOpen} onHide={handleClose}>
      {isLoading && <div style={{
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        zIndex: 9999
      }}>
        <FontAwesomeIcon
          icon={faSpinner}
          spin
          size="5x"
        />
      </div>}
      <Modal.Header style={{
        border: "none",
        opacity: isLoading ? 0.5 : 1,
        padding: 0,
        position: "absolute",
        right: "32px" }} closeButton={!isOnboarding} />
      <Form onSubmit={handleSubmit}>
        <div style={{ display: "flex", flexDirection: "column" , gap: "32px", opacity: isLoading ? 0.5 : 1 }}>
          <img src={OKOCoreLogo} alt="OKO" style={{ alignSelf: "center", width: "196px" }} />
          <div style={{ display: "flex", flexDirection: "column" , gap: "24px" }}>
            <p style={{
              fontSize: "28px",
              fontWeight: 700,
              lineHeight: "36px" }}>
              Educator Settings
            </p>
            <Form.Group>
              <Form.Label style={{ fontWeight: "bold" }}>School</Form.Label>
              {multipleSchools ? (
                <Select
                  aria-label="Select your school"
                  options={schoolOptions}
                  onChange={handleSchoolSelect}
                  placeholder="Select your school"
                  styles={selectStyles}
                  value={schoolOptions.find(o => o.value === selectedSchoolId) || null}
                />
              ) : availableSchools && availableSchools.length > 1 ? (
                <>
                  <Select
                    aria-label="Select your active school"
                    options={availableSchools.map(s => ({ value: s.id, label: s.name }))}
                    onChange={handleActiveSchoolChange}
                    placeholder="Select your active school"
                    styles={selectStyles}
                    isDisabled={isSwitchingSchool}
                    value={
                      availableSchools
                        .filter(s => s.id === activeSchoolId)
                        .map(s => ({ value: s.id, label: s.name }))[0]
                      || null
                    }
                  />
                  <p style={{
                    fontSize: "12px",
                    color: colors.neutral400,
                    marginTop: "4px",
                  }}>
                    You belong to multiple schools. The selected school
                    scopes your dashboard (classes, sessions, reports).
                  </p>
                  {error?.active_school && (
                    <Alert style={alertStyle} variant="danger">{error.active_school}</Alert>
                  )}
                </>
              ) : (
                <Form.Control
                  type="text"
                  value={formData.school_name}
                  disabled
                  style={{ backgroundColor: colors.gray100 }}
                />
              )}
              {error?.school && (
                <Alert style={alertStyle} variant="danger">{error.school}</Alert>
              )}
            </Form.Group>
            <Form.Group>
              <Form.Label style={{ fontWeight: "bold" }}>Grade Selections</Form.Label>
              <Select
                aria-label="Select grade levels"
                components={{ MultiValueRemove }}
                isMulti
                options={getGradeOptions()}
                onChange={handleSelectGradeChange}
                placeholder="Select grades"
                styles={selectStyles}
                value={getGradeOptions().filter((i) => formData.grade_levels.includes(i.value))}>
              </Select>
              {error?.grade_levels && (
                <Alert style={alertStyle} variant="danger">{error.grade_levels}</Alert>
              )}
            </Form.Group>
          </div>
          {isOnboarding ? (<>{saveButton()}</>) : (<>{buttons()}</>)}
        </div>
      </Form>
    </Modal>
  );
};
